import { useEffect, useMemo, useRef, useState } from 'react'
import type { GraphKind, GraphNode } from '@/lib/apiTypes'
import type { Direction } from '@/features/graph/lib/graphView'
import { ChipIcon, DatabaseIcon, BoxIcon } from '@/shared/components/Icons'
import { Tooltip } from '@/shared/components/Tooltip'
import s from '@/features/graph/components/GraphRootPicker.module.css'

/**
 * Jump straight to any node's lineage without hunting for it on the canvas.
 *
 * The cold-start equivalent of clicking a node: picking an entry isolates it exactly as a
 * click would. Grouped by kind, because "switch between state files and modules" is the
 * question this answers, and a flat list of 100+ names does not answer it.
 */

const KIND_GLYPH: Record<GraphKind, React.ReactNode> = {
  state: <DatabaseIcon size={12} />,
  module: <BoxIcon size={12} />,
  provider: <ChipIcon size={12} />,
}

const KIND_GROUP: Record<GraphKind, string> = {
  state: 'State files',
  module: 'Modules',
  provider: 'Providers',
}

const DIRECTIONS: { key: Direction; glyph: string; label: string; tip: string }[] = [
  { key: 'up', glyph: '↑', label: 'Upstream', tip: 'What depends on it — the blast radius if it changes or breaks' },
  { key: 'both', glyph: '↕', label: 'Both', tip: 'Its full lineage: what depends on it and what it depends on' },
  { key: 'down', glyph: '↓', label: 'Downstream', tip: 'What it depends on — its bill of materials' },
]

export function GraphRootPicker({
  nodes, focusId, direction, onPick, onClear, onDirection,
}: {
  nodes: GraphNode[]
  focusId: string | null
  direction: Direction
  onPick: (node: GraphNode) => void
  onClear: () => void
  onDirection: (d: Direction) => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const wrapRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const focused = nodes.find((n) => n.id === focusId) ?? null

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase()
    const match = (n: GraphNode) =>
      !q || n.label.toLowerCase().includes(q) || n.namespace.toLowerCase().includes(q)
    const out: { kind: GraphKind; items: GraphNode[] }[] = []
    for (const kind of ['state', 'module', 'provider'] as GraphKind[]) {
      const items = nodes.filter((n) => n.kind === kind && match(n))
        .sort((a, b) => a.label.localeCompare(b.label))
      if (items.length > 0) out.push({ kind, items })
    }
    return out
  }, [nodes, query])

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { setOpen(false); setQuery('') }
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    inputRef.current?.focus()
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div className={s.wrap}>
      <div ref={wrapRef} className={s.pickerWrap}>
        <button
          type="button"
          className={s.trigger}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <span className={s.triggerLabel}>Root</span>
          {focused ? (
            <>
              <span className={s.triggerGlyph}>{KIND_GLYPH[focused.kind]}</span>
              <span className={s.triggerValue}>{focused.label}</span>
            </>
          ) : (
            <span className={s.triggerEmpty}>whole graph</span>
          )}
          <span className={s.chev}>▾</span>
        </button>

        {open && (
          <div className={s.popover} role="listbox">
            <input
              ref={inputRef}
              className={s.search}
              placeholder="Find a state file, module or provider…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div className={s.list}>
              {focused && (
                <button
                  className={s.clearItem}
                  onClick={() => { onClear(); setOpen(false); setQuery('') }}
                >
                  ✕ Show the whole graph
                </button>
              )}
              {groups.length === 0 && <div className={s.noMatch}>Nothing matches “{query.trim()}”</div>}
              {groups.map(({ kind, items }) => (
                <div key={kind}>
                  <div className={s.groupHead}>{KIND_GROUP[kind]} · {items.length}</div>
                  {items.map((n) => (
                    <button
                      key={n.id}
                      role="option"
                      aria-selected={n.id === focusId}
                      className={`${s.item} ${n.id === focusId ? s.itemOn : ''}`}
                      onClick={() => { onPick(n); setOpen(false); setQuery('') }}
                    >
                      <span className={s.itemGlyph}>{KIND_GLYPH[kind]}</span>
                      <span className={s.itemLabel}>{n.label}</span>
                      {n.kind === 'state' && n.has_lock_file === false && (
                        <span className={s.itemNote}>no lock file</span>
                      )}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Direction is only meaningful once something is isolated. Shown disabled rather than
          hidden, so the control does not appear and disappear as you click around. */}
      <div className={s.dirSeg} role="group" aria-label="Lineage direction">
        {DIRECTIONS.map(({ key, glyph, label, tip }) => (
          <Tooltip key={key} text={tip} placement="bottom" maxWidth={260}>
            <button
              type="button"
              className={`${s.dirBtn} ${direction === key ? s.dirBtnOn : ''}`}
              aria-pressed={direction === key}
              aria-label={label}
              disabled={!focusId}
              onClick={() => onDirection(key)}
            >
              {glyph}
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  )
}
